import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../../../lib/hooks';
import { useCreateBankInstrument } from '../../../../features/finance/hooks';
import { Card, CardContent } from '../../../../components/ui/Card';
import { FormField, Input } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Button } from '../../../../components/ui/Button';
import { useState } from 'react';
import { Decimal } from 'decimal.js';
import { useQuery } from '@tanstack/react-query';
import { fetcher } from '../../../../lib/api-fetcher';

export const Route = createFileRoute('/_auth/finance/cheque-register/new')({
  component: RecordChequePaymentPage,
});

interface ActiveSubscriberOption {
  id: string;
  subscriberId: string;
  customerId: string;
  ticketNumber: number;
  status: string;
  customerName: string;
  customerMobile: string | null;
  schemeId: string;
  schemeName: string;
  schemeNumber: string;
}

const PAYMENT_TYPES = [
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'UPI', label: 'UPI' },
  { value: 'GPAY', label: 'GPay' },
  { value: 'NEFT', label: 'NEFT' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'E_NACH', label: 'E-NACH' },
] as const;

function RecordChequePaymentPage() {
  usePageTitle('Record Cheque / Payment');
  const navigate = useNavigate();
  const mutation = useCreateBankInstrument();
  const { toast } = useToast();

  const [paymentType, setPaymentType] = useState('CHEQUE');
  const [selectedSubscriber, setSelectedSubscriber] = useState<ActiveSubscriberOption | null>(null);
  const [bankName, setBankName] = useState('');
  const [instrumentNumber, setInstrumentNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [accountNumberLast4, setAccountNumberLast4] = useState('');
  const [amountRupees, setAmountRupees] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: subscribersData, isLoading: subscribersLoading } = useQuery({
    queryKey: ['subscribers', 'active'],
    queryFn: async () => {
      const response = await fetcher.get<{ data: ActiveSubscriberOption[] }>('/staff/v1/subscribers/active');
      return response.data;
    },
  });

  const subscribers = subscribersData ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSubscriber) {
      toast({ type: 'error', title: 'Error', description: 'Please select a subscriber.' });
      return;
    }

    let amount: Decimal;
    try {
      amount = new Decimal(amountRupees);
    // allow-silent-catch: user input validation failure is handled via toast
    } catch {
      toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount in rupees.' });
      return;
    }

    const paise = amount.mul(100);

    if (!amount.isFinite() || amount.lte(0) || !paise.isInteger()) {
      toast({ type: 'error', title: 'Error', description: 'Please enter a valid amount in rupees.' });
      return;
    }

    const totalAmountPaise = paise.toNumber();

    if (paymentType === 'CHEQUE' && !chequeDate) {
      toast({ type: 'error', title: 'Error', description: 'Cheque date is required for cheque payments.' });
      return;
    }

    const payload: Record<string, unknown> = {
      customerId: selectedSubscriber.customerId,
      transactionDate,
      paymentType,
      instrumentNumber: instrumentNumber || undefined,
      totalAmountPaise,
      othersAmountPaise: 0,
      sendSms: false,
      allocations: [
        {
          allocationType: 'INSTALLMENT',
          subscriptionId: selectedSubscriber.id,
          amountPaise: totalAmountPaise,
        },
      ],
    };

    if (paymentType === 'CHEQUE') {
      payload.chequeDate = chequeDate;
    }

    if (bankName) {
      payload.bankName = bankName;
    }

    if (accountNumberLast4) {
      payload.bankAccountNumber = accountNumberLast4;
    }

    mutation.mutate(payload, {
      onSuccess: () => {
        toast({ type: 'success', title: 'Success', description: 'Payment recorded successfully.' });
        navigate({ to: '/finance/cheque-register' });
      },
      onError: (error: { message?: string }) => {
        toast({ type: 'error', title: 'Error', description: error?.message || 'Failed to record payment.' });
      },
    });
  };

  const showInstrumentNumber = !['CASH'].includes(paymentType);
  const showChequeDate = paymentType === 'CHEQUE';
  const showBankName = ['CHEQUE', 'NEFT', 'RTGS', 'E_NACH'].includes(paymentType);
  const showAccountNumber = ['CHEQUE', 'NEFT', 'RTGS'].includes(paymentType);

  return (
    <FormPageLayout
      title="Record Cheque / Payment"
      backTo="/finance/cheque-register"
      variant="plain"
    >
      <Card className="max-w-[640px]">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <FormSelect
              label="Payment Type"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              options={PAYMENT_TYPES.map((pt) => ({ value: pt.value, label: pt.label }))}
              required
            />

            <FormField label="Subscriber" required>
              <select
                className="border border-border w-full h-9 px-3 rounded bg-surface-1 text-text-primary focus:ring-1 focus:ring-accent-500 focus:outline-none text-sm font-semibold"
                value={selectedSubscriber?.id ?? ''}
                onChange={(e) => {
                  const sub = subscribers.find((s) => s.id === e.target.value);
                  setSelectedSubscriber(sub ?? null);
                }}
                disabled={subscribersLoading}
                required
              >
                <option value="">-- Select Subscriber --</option>
                {subscribers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.customerName} (Ticket: {s.ticketNumber}) - {s.schemeName} [{s.schemeNumber}]
                  </option>
                ))}
              </select>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Transaction Date" required>
                <Input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Amount (₹)" required>
                <Input
                  type="number"
                  value={amountRupees}
                  onChange={(e) => setAmountRupees(e.target.value)}
                  placeholder="5000"
                  min="0.01"
                  step="0.01"
                  required
                />
              </FormField>
            </div>

            {showBankName && (
              <FormField label="Bank Name">
                <Input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="e.g. State Bank of India"
                />
              </FormField>
            )}

            {showInstrumentNumber && (
              <FormField label={paymentType === 'CHEQUE' ? 'Cheque Number' : paymentType === 'UPI' || paymentType === 'GPAY' ? 'UPI / Transaction Ref' : 'Transaction Reference'} required>
                <Input
                  type="text"
                  value={instrumentNumber}
                  onChange={(e) => setInstrumentNumber(e.target.value)}
                  placeholder={
                    paymentType === 'CHEQUE' ? '123456' :
                    paymentType === 'UPI' || paymentType === 'GPAY' ? 'upi@bank' :
                    'Enter reference number'
                  }
                  required
                />
              </FormField>
            )}

            {showChequeDate && (
              <FormField label="Cheque Date">
                <Input
                  type="date"
                  value={chequeDate}
                  onChange={(e) => setChequeDate(e.target.value)}
                />
              </FormField>
            )}

            {showAccountNumber && (
              <FormField label="Account Number (Last 4 Digits)">
                <Input
                  type="text"
                  value={accountNumberLast4}
                  onChange={(e) => setAccountNumberLast4(e.target.value)}
                  placeholder="1234"
                  maxLength={4}
                />
              </FormField>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate({ to: '/finance/cheque-register' })}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Saving...' : 'Record Payment'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </FormPageLayout>
  );
}
